import { useCallback, memo, useMemo, useState, useRef, useEffect } from 'react';
import { Card } from '@bancobarigui/barihaus';
import { Checkbox, Tooltip, message, Form } from 'antd';
import { HolderOutlined, CameraOutlined, LinkOutlined } from '@ant-design/icons';
import { AmostraCard } from './AmostraCard';
import { AmostraModal } from './AmostraCard/components/AmostraModal';
import type { AmostraPadrao } from '@/features/laudo/mock/amostrasPadronizadas';
import { inspectionClient } from '@/config/api';

// Cache em memória para imagens scraped
const imageCache = new Map<string, string>();

function ScrapedImage({ externalId, imagemUrl, style }: { externalId?: string; imagemUrl?: string; style?: React.CSSProperties }) {
  const [src, setSrc] = useState<string | undefined>(() => {
    if (imagemUrl?.startsWith('data:image/')) return imagemUrl;
    if (externalId && imageCache.has(externalId)) return imageCache.get(externalId);
    return undefined;
  });

  useEffect(() => {
    if (!externalId || src) return;
    let cancelled = false;
    inspectionClient.get(`/api/scraped-properties/${externalId}/image`, { responseType: 'blob' })
      .then(response => {
        if (cancelled) return;
        const reader = new FileReader();
        reader.onloadend = () => {
          if (cancelled) return;
          const base64 = reader.result as string;
          imageCache.set(externalId, base64);
          setSrc(base64);
        };
        reader.readAsDataURL(response.data as Blob);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [externalId, src]);

  if (!src) return null;
  return <img src={src} alt="" style={style} loading="lazy" onError={(e) => { e.currentTarget.style.display = 'none'; }} />;
}

interface AmostrasListProps {
  amostras: AmostraPadrao[];
  onAmostrasReorder: (amostras: AmostraPadrao[]) => void;
  onEdit: (amostra: AmostraPadrao) => void;
  onDelete: (identificacao: string) => void;
  onSelectionChange: (identificacao: string, selected: boolean) => void;
  onImageUpload?: (identificacao: string, imageUrl: string) => void;
  metodologia: string;
  hideActions: boolean;
  disabled: boolean;
}

// Cores discretas para ranking
const RANKING_COLORS: Record<number, { bg: string; border: string; text: string }> = {
  1: { bg: '#fffbe6', border: '#faad14', text: '#d48806' },
  2: { bg: '#f5f5f5', border: '#8c8c8c', text: '#595959' },
  3: { bg: '#fff7e6', border: '#d4a574', text: '#ad6800' },
};
const NEUTRAL_COLOR = { bg: '#e6f4ff', border: '#91caff', text: '#0958d9' };

function AmostrasListComponent({
  amostras,
  onAmostrasReorder,
  onEdit,
  onDelete,
  onSelectionChange,
  onImageUpload,
  metodologia,
  hideActions,
  disabled,
}: AmostrasListProps) {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingFor, setUploadingFor] = useState<string | null>(null);
  const [editingAmostra, setEditingAmostra] = useState<AmostraPadrao | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editForm] = Form.useForm();

  const amostrasParaExibir = useMemo(
    () => amostras.filter((a) => a.identificacao !== 'Avaliando'),
    [amostras]
  );

  const handleDragStart = useCallback((e: React.DragEvent<HTMLDivElement>, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
    requestAnimationFrame(() => {
      e.currentTarget.style.opacity = '0.5';
    });
  }, []);

  const handleDragEnd = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.currentTarget.style.opacity = '1';
    setDraggedIndex(null);
    setDragOverIndex(null);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  }, [draggedIndex]);

  const handleDragLeave = useCallback(() => {
    setDragOverIndex(null);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>, dropIndex: number) => {
    e.preventDefault();
    const fromIndex = draggedIndex;

    if (fromIndex === null || fromIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newAmostras = [...amostrasParaExibir];
    const [removed] = newAmostras.splice(fromIndex, 1);
    newAmostras.splice(dropIndex, 0, removed);

    const amostrasComRanking = newAmostras.map((amostra, idx) => ({
      ...amostra,
      rankingHomogeneizacao: idx + 1,
    })) as AmostraPadrao[];

    onAmostrasReorder(amostrasComRanking);
    setDraggedIndex(null);
    setDragOverIndex(null);
  }, [draggedIndex, amostrasParaExibir, onAmostrasReorder]);

  // Handler para upload de imagem
  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !uploadingFor) return;

    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('Apenas arquivos de imagem são permitidos!');
      return;
    }

    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error('A imagem deve ter menos de 5MB!');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target?.result as string;
      onImageUpload?.(uploadingFor, base64);
      setUploadingFor(null);
    };
    reader.readAsDataURL(file);

    // Limpa o input para permitir selecionar o mesmo arquivo novamente
    e.target.value = '';
  }, [uploadingFor, onImageUpload]);

  const triggerFileUpload = useCallback((identificacao: string) => {
    setUploadingFor(identificacao);
    fileInputRef.current?.click();
  }, []);

  // Handlers para edição de amostra
  const handleEditClick = useCallback((amostra: AmostraPadrao) => {
    setEditingAmostra(amostra);
    editForm.setFieldsValue(amostra);
    setIsEditModalOpen(true);
  }, [editForm]);

  const handleSaveEdit = useCallback(async () => {
    if (!editingAmostra) return;

    try {
      const values = await editForm.validateFields();
      // Pega imagemUrl explicitamente (pode não vir no validateFields se o Form.Item usa componente customizado)
      const imagemUrl = editForm.getFieldValue('imagemUrl');
      onEdit({ ...editingAmostra, ...values, imagemUrl });
      setIsEditModalOpen(false);
      setEditingAmostra(null);
    } catch (error) {
      // Validation failed
    }
  }, [editingAmostra, editForm, onEdit]);

  const handleCancelEdit = useCallback(() => {
    setIsEditModalOpen(false);
    setEditingAmostra(null);
  }, []);

  if (disabled) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {amostrasParaExibir.map((amostra) => (
          <AmostraCard
            key={amostra.identificacao}
            amostra={amostra}
            onEdit={onEdit}
            onDelete={onDelete}
            metodologia={metodologia}
            hideActions={hideActions}
            showRanking={true}
            showSelectionCheckbox={true}
            onSelectionChange={onSelectionChange}
          />
        ))}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {/* Input hidden para upload de arquivo */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
      {amostrasParaExibir.map((amostra, index) => {
        const ranking = amostra.rankingHomogeneizacao;
        const rankingColor = ranking ? (RANKING_COLORS[ranking] || NEUTRAL_COLOR) : null;
        const isSelected = amostra.selecionadaHomogeneizacao ?? false;
        const isDragOver = dragOverIndex === index;
        const isDragging = draggedIndex === index;

        return (
          <div
            key={amostra.identificacao}
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragEnd={handleDragEnd}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, index)}
            style={{
              marginBottom: 12,
              opacity: isDragging ? 0.5 : 1,
              transform: isDragOver ? 'translateY(4px)' : 'none',
              transition: 'transform 0.15s ease',
              borderTop: isDragOver && draggedIndex !== null && draggedIndex > index
                ? '3px solid #1890ff'
                : 'none',
              borderBottom: isDragOver && draggedIndex !== null && draggedIndex < index
                ? '3px solid #1890ff'
                : 'none',
              cursor: 'grab',
            }}
          >
            <Card
              style={{
                borderRadius: 8,
                border: '1px solid #d9d9d9',
                transition: 'box-shadow 0.2s, border-color 0.2s',
                ...(isDragging ? { boxShadow: '0 8px 24px rgba(0,0,0,0.15)' } : {}),
                ...(isSelected ? {
                  borderColor: '#52c41a',
                  borderWidth: 2,
                  backgroundColor: '#f6ffed',
                } : {})
              }}
              bodyStyle={{ padding: 12 }}
            >
              <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                {/* Handle de drag */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: 8,
                    cursor: 'grab',
                    color: '#bfbfbf',
                    padding: '4px 0',
                  }}
                >
                  <HolderOutlined style={{ fontSize: 18 }} />
                </div>

                {/* Badge de ranking */}
                {ranking && (
                  <Tooltip title={`Posição ${ranking} no ranking`}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: 24,
                      height: 24,
                      borderRadius: 4,
                      backgroundColor: rankingColor?.bg,
                      border: `1px solid ${rankingColor?.border}`,
                      color: rankingColor?.text,
                      fontWeight: 600,
                      fontSize: 12,
                      marginRight: 8,
                    }}>
                      {ranking}º
                    </div>
                  </Tooltip>
                )}

                {/* Checkbox de seleção */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginRight: 12,
                  paddingRight: 12,
                  borderRight: '1px solid #f0f0f0'
                }}>
                  <Checkbox
                    checked={isSelected}
                    onChange={(e) => {
                      e.stopPropagation();
                      onSelectionChange(amostra.identificacao, e.target.checked);
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>

                {/* Conteúdo do card */}
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 16 }}>
                  {/* Thumbnail com opção de upload */}
                  <div style={{
                    width: 64,
                    height: 64,
                    borderRadius: 6,
                    backgroundColor: '#f5f5f5',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    overflow: 'hidden',
                    position: 'relative',
                    cursor: !amostra.imagemUrl && !hideActions ? 'pointer' : 'default',
                    border: !amostra.imagemUrl ? '1px dashed #d9d9d9' : 'none',
                    transition: 'border-color 0.2s',
                  }}
                  onClick={(e) => {
                    if (!amostra.imagemUrl && !hideActions) {
                      e.stopPropagation();
                      triggerFileUpload(amostra.identificacao);
                    }
                  }}
                  onMouseEnter={(e) => {
                    if (!amostra.imagemUrl && !hideActions) {
                      e.currentTarget.style.borderColor = '#1890ff';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!amostra.imagemUrl && !hideActions) {
                      e.currentTarget.style.borderColor = '#d9d9d9';
                    }
                  }}
                  >
                    {amostra.imagemUrl || amostra.externalId ? (
                      <ScrapedImage
                        externalId={amostra.externalId}
                        imagemUrl={amostra.imagemUrl}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <Tooltip title={hideActions ? '' : 'Clique para adicionar foto'}>
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}>
                          <CameraOutlined style={{ color: '#bfbfbf', fontSize: 20 }} />
                          {!hideActions && (
                            <span style={{ color: '#bfbfbf', fontSize: 10, marginTop: 2 }}>Foto</span>
                          )}
                        </div>
                      </Tooltip>
                    )}
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>
                      {amostra.identificacao} - {amostra.tipoImovel}
                    </div>
                    <div style={{ fontSize: 13, color: '#595959' }}>
                      {amostra.areaPrivativa > 0 && `${amostra.areaPrivativa}m² • `}
                      {amostra.valorTotal > 0 && `R$ ${amostra.valorTotal.toLocaleString('pt-BR')}`}
                    </div>
                    {amostra.endereco && (
                      <div style={{
                        fontSize: 12,
                        color: '#8c8c8c',
                        marginTop: 2,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                        {amostra.endereco}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  {!hideActions && (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      {amostra.urlOriginal && (
                        <Tooltip title="Abrir anúncio em nova aba">
                          <a
                            href={amostra.urlOriginal}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#1890ff',
                              fontSize: 16,
                              padding: '4px 8px',
                              lineHeight: 1,
                            }}
                          >
                            <LinkOutlined />
                          </a>
                        </Tooltip>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditClick(amostra);
                        }}
                        style={{
                          border: 'none',
                          background: 'none',
                          cursor: 'pointer',
                          color: '#1890ff',
                          fontSize: 14,
                          padding: '4px 8px',
                        }}
                      >
                        Editar
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(amostra.identificacao);
                        }}
                        style={{
                          border: 'none',
                          background: 'none',
                          cursor: 'pointer',
                          color: '#ff4d4f',
                          fontSize: 14,
                          padding: '4px 8px',
                        }}
                      >
                        Excluir
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          </div>
        );
      })}

      {/* Modal de edição de amostra */}
      {editingAmostra && (
        <AmostraModal
          amostra={editingAmostra}
          isOpen={isEditModalOpen}
          form={editForm}
          onSave={handleSaveEdit}
          onCancel={handleCancelEdit}
          metodologia={metodologia}
        />
      )}
    </div>
  );
}

export const AmostrasList = memo(AmostrasListComponent);
